/* global dataLayer */
import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, useWatch } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useStateMachine } from 'little-state-machine';
import { useRouter } from 'next/router';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import _ from 'lodash';
import Spar from 'Components/Spar';
import CompanyDataFields from 'Components/CompanyDataFields';
import Fieldset from 'Components/Fieldset';
import FieldRow from 'Components/FieldRow';
import Field from 'Components/Field';
import Loader from 'Components/Loader';
import ButtonNext from 'Components/ButtonNext';
import checkPhone from 'utils/checkPhone';
import checkOrgNr from 'utils/checkOrgNr';
import formatSsn from 'utils/formatSsn';
import updateAction from 'utils/updateAction';
import s from './Step2.module.scss';

const Step2 = ({ formNamespace, countries, texts }) => {
    const router = useRouter();
    const updateName = (state, payload) => {
        return updateAction(state, payload, formNamespace);
    };
    const { state, actions } = useStateMachine({ updateName });

    const currState = _.get(state, formNamespace, {});

    const [loading, setLoading] = useState(false);

    const schema = yup.object().shape({
        companyName: yup.string().required('Vänligen fyll i företagsnamn'),
        orgNumber: yup.string()
            .required('Vänligen fyll i organisationsnummer')
            .matches(/^[0-9]+$/, 'Vänligen ange ett giltigt organisationsnummer med endast siffror')
            .min(10, 'Vänligen ange 10 siffror för organisationsnummer')
            .max(10, 'Vänligen ange 10 siffror för organisationsnummer')
            .test(
                'luhn',
                'Vänligen ange ett giltigt organisationsnummer',
                (value) => checkOrgNr(value)
            ),
        address: yup.string().required('Vänligen fyll i adress'),
        postalCode: yup.string().required('Vänligen fyll i postnummer'),
        city: yup.string().required('Vänligen fyll i en giltig postort'),
        country: yup.object()
            .shape({
                value: yup.string(),
                label: yup.string(),
            })
            .nullable()
            .required('Vänligen välj ett land'),
        email: yup
            .string()
            .email('Vänligen fyll i en giltig e-postadress')
            .required('Vänligen fyll i en giltig e-postadress'),
        phone: yup
            .string()
            .test(
                'validPhone',
                'Vänligen fyll i ett giltigt telefonnummer: +46 73 123 45 67',
                checkPhone
            ),
    });

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        formState,
        trigger,
        control,
        watch,
    } = useForm({
        mode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: currState,
    });
    const { errors, isValid } = formState;

    const onSubmit = (data) => {
        dataLayer.push({
            event: 'checkoutSteps',
            eventCategory: 'företagsgåva',
            eventAction: 'företagsuppgifter',
        });

        Object.assign(data, { steg2: true });
        actions.updateName(data);
        router.push({hash: 'steg3'});
    };

    /* eslint-disable react-hooks/rules-of-hooks */
    return (
        <form className={s['Step2']} onSubmit={handleSubmit(onSubmit)}>
            <Fieldset title="Företagsinformation">
                <CompanyDataFields
                    register={register}
                    control={control}
                    errors={errors}
                    countries={countries}
                />
            </Fieldset>

            <Fieldset title="Kontaktinformation">
                <FieldRow>
                    <Field
                        name="email"
                        label="E-post"
                        placeholder=""
                        required={true}
                        register={register('email', {required: true})}
                        errors={errors}
                        width="Full"
                    />

                    <Field
                        name="phone"
                        label="Mobilnummer"
                        placeholder="+46 73 123 45 67"
                        required={true}
                        register={register('phone', {required: true})}
                        errors={errors}
                        width="Full"
                    />
                </FieldRow>

                <Field
                    name="reference"
                    label="Fakturareferens"
                    placeholder=""
                    register={register('reference')}
                    errors={errors}
                />
            </Fieldset>

            <ButtonNext disabled={!isValid}>Nästa steg</ButtonNext>
        </form>
    );
    /* eslint-enable react-hooks/rules-of-hooks */
};

Step2.propTypes = {
    formNamespace: PropTypes.string,
    countries: PropTypes.array,
    texts: PropTypes.object,
};


export default Step2;
